// Strokes for a classified restriction (util/restrictionClass.mjs), shared by the LM
// lane layers (hdLaneRestriction.js) and the RM/OSMLR Restrictions view
// (sdLaneRestriction.js) so the same restriction looks the same in all of them:
//   access only              the access colour, `casing` wide
//   HOV only                 orange `casing` wide, dashed when time-conditional
//   access + HOV             the access colour with an orange `core` (dashed when
//                            conditional): California HOV lanes are mostly also no-trucks
//   HOV tagged, no HOV lane  orange dots, over the plain stroke or along the access colour
// Only the widths differ per model: LM lanes lie side by side and would merge at 8px.
import { Stroke, Style } from "ol/style";
import { Z_RESTRICTION } from "./roadClass";

export const RM_WIDTHS = { casing: 8, core: 4, dots: 3 };
export const LM_WIDTHS = { casing: 6, core: 3, dots: 2 };

const HOV_COLOR = "orange";
// Per-way sibling of the band view's CONDITIONAL_DASH: a dashed stroke says "this
// restriction holds only during its posted window".
const CONDITIONAL_DASH = [14, 8];
// Zero-length dashes with round caps draw as dots: "an HOV tag is here, and says no
// lane is HOV" (`|||`, `no|no`) — a different shape from both HOV strokes.
const EMPTY_HOV_DOTS = [0.1, 7];
// Hover and click add a halo UNDER the strokes, as the Lanes view does, so a selected
// way still shows which of HOV / access it carries. The click halo is a colour the
// palette doesn't use.
const HALO_EXTRA_PX = 4;
const HOVER_HALO = "white";
const CLICK_HALO = "#00b8d9";

// Bottom to top.
function restrictedStrokes({ access, hov }, plain, widths) {
  const strokes = [];
  if (access) strokes.push({ color: access, width: widths.casing });
  if (hov === "active" || hov === "conditional") {
    strokes.push({
      color: HOV_COLOR,
      width: access ? widths.core : widths.casing,
      lineDash: hov === "conditional" ? CONDITIONAL_DASH : undefined,
    });
  }
  if (hov === "empty") {
    if (!access) strokes.push(...plain.strokes);
    strokes.push({
      color: HOV_COLOR,
      width: widths.dots,
      lineDash: EMPTY_HOV_DOTS,
      lineCap: "round",
    });
  }
  return { zIndex: Z_RESTRICTION, strokes };
}

/**
 * @param {{access: string|null, hov: string|null}|null} restriction — from
 *   classifyLmRestriction / classifyRmRestriction; null draws `plain`
 * @param {import("ol/geom/LineString").default|undefined} geometry — undefined = the feature's own
 * @param {{plain: {zIndex?: number, strokes: object[]}, widths: {casing: number,
 *   core: number, dots: number}, click?: boolean, hover?: boolean}} options
 * @returns {Style[]}
 */
export function restrictionStyles(
  restriction,
  geometry,
  { plain, widths, click = false, hover = false },
) {
  const { zIndex, strokes } = restriction
    ? restrictedStrokes(restriction, plain, widths)
    : plain;
  const styles = strokes.map(
    ({ color, width, lineDash, lineCap }) =>
      new Style({
        geometry,
        zIndex,
        stroke: new Stroke({ color, width, lineDash, lineCap }),
      }),
  );
  if (!click && !hover) return styles;
  const widest = Math.max(...strokes.map((s) => s.width));
  return [
    new Style({
      geometry,
      zIndex,
      stroke: new Stroke({
        color: click ? CLICK_HALO : HOVER_HALO,
        width: widest + HALO_EXTRA_PX,
      }),
    }),
    ...styles,
  ];
}
